import React, { useCallback, useEffect, forwardRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * Bottom Sheet Component - Modal nativo para mobile
 * Sigue el patrón UX de WhatsApp, Telegram, etc.
 * 
 * @component
 * @param {boolean} isOpen - Controla visibilidad
 * @param {string} timestamp - ID de la conversación
 * @param {boolean} isDeleting - Estado de confirmación
 * @param {Function} onDelete - Handler de eliminación
 * @param {Function} onClose - Handler de cierre
 */
const BottomSheet = forwardRef(({
    isOpen,
    timestamp,
    isDeleting,
    onDelete,
    onClose
}, ref) => {
    const { t } = useTranslation();

    // Handler de eliminación con prevención de propagación
    const handleDelete = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        onDelete(e, timestamp);
    }, [timestamp, onDelete]);

    // Handler de overlay (cerrar al hacer click fuera)
    const handleOverlayClick = useCallback((e) => {
        e.stopPropagation();
        onClose();
    }, [onClose]);

    // Prevenir propagación en el sheet
    const handleSheetClick = useCallback((e) => {
        e.stopPropagation();
    }, []);

    // ✅ Bloquear scroll del body cuando está abierto
    useEffect(() => {
        if (isOpen) {
            const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
            document.body.style.overflow = 'hidden';
            document.body.style.paddingRight = `${scrollBarWidth}px`;
        } else {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [isOpen]);

    // ✅ Cerrar con tecla ESC
    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return createPortal(
        <div
            className="bottom-sheet-container"
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 999998,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center'
            }}
        >
            {/* Overlay con fade in */}
            <div
                className="bottom-sheet-overlay"
                style={{
                    position: 'absolute',
                    inset: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    animation: 'fadeIn 0.25s ease-out',
                    backdropFilter: 'blur(2px)'
                }}
                onClick={handleOverlayClick}
                aria-hidden="true"
            />

            {/* Bottom Sheet */}
            <div
                ref={ref}
                className="bottom-sheet"
                style={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: '500px',
                    backgroundColor: '#202123',
                    borderTopLeftRadius: '20px',
                    borderTopRightRadius: '20px',
                    padding: '24px 20px',
                    paddingBottom: 'calc(24px + env(safe-area-inset-bottom))',
                    zIndex: 999999,
                    animation: 'slideUp 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                    boxShadow: '0 -4px 24px rgba(0, 0, 0, 0.5)',
                    maxHeight: '80vh',
                    overflowY: 'auto'
                }}
                onClick={handleSheetClick}
                role="dialog"
                aria-modal="true"
                aria-labelledby="bottom-sheet-title"
            >
                {/* Handle bar - indicador visual de que se puede arrastrar */}
                <div
                    style={{
                        width: '40px',
                        height: '4px',
                        backgroundColor: '#565869',
                        borderRadius: '2px',
                        margin: '0 auto 20px',
                        flexShrink: 0
                    }}
                    aria-hidden="true"
                />

                {/* Title */}
                <h4
                    id="bottom-sheet-title"
                    style={{
                        margin: '0 0 20px',
                        color: '#ececf1',
                        fontSize: '18px',
                        fontWeight: '600',
                        letterSpacing: '-0.3px'
                    }}
                >
                    {t('chat.conversation_actions', 'Conversation Actions')}
                </h4>

                {/* Delete Button */}
                <button
                    onClick={handleDelete}
                    style={{
                        // display: 'flex',
                        // alignItems: 'center',
                        // gap: '14px',
                        width: '100%',
                        padding: '18px 20px',
                        backgroundColor: isDeleting ? '#c0392b' : 'rgba(231, 76, 60, 0.1)',
                        border: isDeleting ? 'none' : '1px solid rgba(231, 76, 60, 0.3)',
                        borderRadius: '14px',
                        color: isDeleting ? 'white' : '#e74c3c',
                        fontSize: '16px',
                        fontWeight: isDeleting ? '700' : '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        boxShadow: isDeleting ? '0 4px 12px rgba(192, 57, 43, 0.3)' : 'none',
                        transform: isDeleting ? 'scale(1.02)' : 'scale(1)'
                    }}
                    onTouchStart={(e) => {
                        if (!isDeleting) {
                            e.currentTarget.style.backgroundColor = 'rgba(231, 76, 60, 0.2)';
                        }
                    }}
                    onTouchEnd={(e) => {
                        if (!isDeleting) {
                            e.currentTarget.style.backgroundColor = 'rgba(231, 76, 60, 0.1)';
                        }
                    }}
                    aria-label={isDeleting
                        ? t('chat.sidebar_delete_confirmation', 'Confirm deletion')
                        : t('chat.sidebar_delete', 'Delete conversation')
                    }
                >
                    {/* <i
                        className={isDeleting ? "bi bi-exclamation-triangle-fill text-center" : "bi bi-trash text-center"}
                        style={{ fontSize: '20px', flexShrink: 0 }}
                    /> */}
                    <span style={{ flex: 1, textAlign: 'left' }}>
                        {isDeleting
                            ? t('chat.sidebar_delete_confirmation', 'Confirm deletion')
                            : t('chat.sidebar_delete', 'Delete conversation')
                        }
                    </span>
                </button>

                {/* Info text cuando está en modo confirmación */}
                {isDeleting && (
                    <p style={{
                        margin: '12px 0 0',
                        fontSize: '13px',
                        color: '#9b9c9f',
                        textAlign: 'center',
                        animation: 'fadeIn 0.2s ease-out'
                    }}>
                        {t('chat.delete_warning', 'This action cannot be undone')}
                    </p>
                )}

                {/* Cancel Button */}
                <button
                    onClick={onClose}
                    style={{
                        width: '100%',
                        padding: '18px 20px',
                        marginTop: '12px',
                        backgroundColor: 'transparent',
                        border: '1px solid #565869',
                        borderRadius: '14px',
                        color: '#ececf1',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}
                    onTouchStart={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                    }}
                    onTouchEnd={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                    aria-label={t('chat.cancel', 'Cancel')}
                >
                    {t('chat.cancel', 'Cancel')}
                </button>
            </div>
        </div>,
        document.body
    );
});

BottomSheet.displayName = 'BottomSheet';

export default React.memo(BottomSheet);